import { validateBannerImage } from '@/domain/use-cases/validate-banner-image';
import ImageCropModal from '@/presentation/components/image-crop-modal';
import React, { useState } from 'react';
import { FiUpload } from 'react-icons/fi';
import { MdCheckCircle, MdImage, MdWarning } from 'react-icons/md';
import { toast } from 'react-toastify';

interface BannerImageUploadProps {
  onFileChange: (file: File | null) => void;
  imageUrl?: string | null;
  inputId?: string;
}

/**
 * COMPONENTE: BannerImageUpload
 *
 * Upload de imagen para banners con validaciones estrictas
 *
 * VALIDACIONES:
 * - Formato: JPG, PNG, WebP únicamente
 * - Tamaño: Máximo 2MB (compresión automática)
 * - Ratio: 3:1 (±10% tolerancia)
 */
const BannerImageUpload: React.FC<BannerImageUploadProps> = ({
  onFileChange,
  imageUrl,
  inputId = 'banner-image-upload',
}) => {
  const [previewURL, setPreviewURL] = useState<string | null>(imageUrl || null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationInfo, setValidationInfo] = useState<{
    width?: number;
    height?: number;
    ratio?: number;
    size?: string;
  } | null>(null);

  // Estados para el modal de recorte
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [originalFileName, setOriginalFileName] = useState<string>('');

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsValidating(true);
    setValidationInfo(null);
    setOriginalFileName(file.name);

    try {
      // Validar imagen completa
      const validation = await validateBannerImage(file);

      if (!validation.isValid) {
        // Verificar si el único error es el ratio de aspecto
        const hasOnlyRatioError = validation.errors.some((error) =>
          error.includes('relación de aspecto'),
        );

        if (hasOnlyRatioError && validation.errors.length === 1) {
          // Ofrecer recortar la imagen
          const tempUrl = URL.createObjectURL(file);
          setImageToCrop(tempUrl);
          setShowCropModal(true);
          setIsValidating(false);
          return;
        }

        // Otros errores - mostrar y rechazar
        validation.errors.forEach((error) => {
          toast.error(error, { theme: 'colored' });
        });
        onFileChange(null);
        setPreviewURL(null);
        setIsValidating(false);
        return;
      }

      // Imagen válida - procesar
      await processValidImage(validation.processedFile || file);
    } catch (error) {
      console.error('Error validating image:', error);
      toast.error('Error al procesar la imagen', { theme: 'colored' });
      onFileChange(null);
      setPreviewURL(null);
    } finally {
      setIsValidating(false);
    }
  };

  /**
   * Procesa una imagen válida y la muestra
   */
  const processValidImage = async (processedFile: File) => {
    const preview = URL.createObjectURL(processedFile);
    setPreviewURL(preview);

    // Obtener info de la imagen
    const img = new Image();
    img.onload = () => {
      setValidationInfo({
        width: img.width,
        height: img.height,
        ratio: parseFloat((img.width / img.height).toFixed(2)),
        size: (processedFile.size / 1024 / 1024).toFixed(2),
      });
    };
    img.src = preview;

    // Pasar archivo al componente padre
    onFileChange(processedFile);

    toast.success('Imagen válida cargada correctamente', {
      theme: 'colored',
    });
  };

  /**
   * Maneja la imagen recortada del modal
   */
  const handleCropComplete = async (croppedImageBlob: Blob) => {
    try {
      // Convertir blob a File
      const croppedFile = new File(
        [croppedImageBlob],
        originalFileName || 'banner-cropped.jpg',
        { type: 'image/jpeg' },
      );

      // Cerrar modal
      setShowCropModal(false);
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
      }
      setImageToCrop(null);

      // Validar la imagen recortada (debería pasar ahora)
      const validation = await validateBannerImage(croppedFile);

      if (!validation.isValid) {
        validation.errors.forEach((error) => {
          toast.error(error, { theme: 'colored' });
        });
        return;
      }

      // Procesar imagen recortada
      await processValidImage(validation.processedFile || croppedFile);
      toast.success('Imagen recortada y procesada correctamente', {
        theme: 'colored',
      });
    } catch (error) {
      console.error('Error processing cropped image:', error);
      toast.error('Error al procesar la imagen recortada', {
        theme: 'colored',
      });
    }
  };

  /**
   * Cancela el recorte
   */
  const handleCropCancel = () => {
    setShowCropModal(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setImageToCrop(null);
    setOriginalFileName('');
  };

  const handleRemoveImage = () => {
    setPreviewURL(null);
    setValidationInfo(null);
    onFileChange(null);
  };

  return (
    <div className="space-y-4">
      {/* Área de upload */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
        <input
          type="file"
          id={inputId}
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
          disabled={isValidating}
        />

        {!previewURL ? (
          <label
            htmlFor={inputId}
            className="flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors rounded-lg p-8"
          >
            <FiUpload className="text-5xl text-gray-400 mb-3" />
            <p className="text-gray-700 font-medium mb-1">
              {isValidating
                ? 'Validando imagen...'
                : 'Haz clic para seleccionar imagen'}
            </p>
            <p className="text-sm text-gray-500 text-center">
              JPG, PNG o WebP • Max 2MB • Ratio 2:1
            </p>
          </label>
        ) : (
          <div className="space-y-4">
            {/* Preview de la imagen */}
            <div className="relative">
              <img
                src={previewURL}
                alt="Preview banner"
                className="w-full rounded-lg shadow-md"
                style={{ aspectRatio: '2/1', objectFit: 'cover' }}
              />
              <button
                onClick={handleRemoveImage}
                className="absolute top-2 right-2 bg-red-500 text-white px-3 py-1 rounded-md text-sm hover:bg-red-600 transition-colors"
              >
                Eliminar
              </button>
            </div>

            {/* Información de validación */}
            {validationInfo && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <MdCheckCircle className="text-green-500 text-xl mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-green-800 mb-2">
                      Imagen validada correctamente
                    </h4>
                    <div className="grid grid-cols-2 gap-2 text-sm text-green-700">
                      <div>
                        <span className="font-medium">Dimensiones:</span>{' '}
                        {validationInfo.width}x{validationInfo.height}px
                      </div>
                      <div>
                        <span className="font-medium">Ratio:</span>{' '}
                        {validationInfo.ratio}:1
                      </div>
                      <div>
                        <span className="font-medium">Tamaño:</span>{' '}
                        {validationInfo.size}MB
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Botón para cambiar imagen */}
            <label
              htmlFor={inputId}
              className="block text-center text-sm text-primary hover:text-primary-dark cursor-pointer font-medium"
            >
              Cambiar imagen
            </label>
          </div>
        )}
      </div>

      {/* Requisitos */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <MdImage className="text-blue-500 text-xl mt-0.5" />
          <div className="flex-1">
            <h4 className="font-semibold text-blue-800 mb-2">
              Requisitos de la imagen
            </h4>
            <ul className="space-y-1 text-sm text-blue-700">
              <li className="flex items-center gap-2">
                <MdCheckCircle className="text-blue-500" />
                Formatos permitidos: JPG, PNG, WebP
              </li>
              <li className="flex items-center gap-2">
                <MdCheckCircle className="text-blue-500" />
                Tamaño máximo: 2MB (compresión automática)
              </li>
              <li className="flex items-center gap-2">
                <MdCheckCircle className="text-blue-500" />
                Relación de aspecto requerida: 2:1 (±10% tolerancia)
              </li>
              <li className="flex items-center gap-2 text-blue-600">
                <MdImage className="text-blue-500" />
                Si la imagen no tiene el ratio correcto, podrás ajustarla con
                nuestro editor
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Preview responsive */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <MdWarning className="text-yellow-500" />
          Vista previa responsive
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Desktop */}
          <div>
            <p className="text-xs text-gray-600 mb-2">Desktop</p>
            {previewURL ? (
              <img
                src={previewURL}
                alt="Preview desktop"
                className="w-full rounded border border-gray-300"
                style={{ aspectRatio: '2/1', objectFit: 'cover' }}
              />
            ) : (
              <div
                className="w-full bg-gray-200 rounded flex items-center justify-center"
                style={{ aspectRatio: '2/1' }}
              >
                <MdImage className="text-gray-400 text-3xl" />
              </div>
            )}
          </div>

          {/* Mobile */}
          <div>
            <p className="text-xs text-gray-600 mb-2">Mobile</p>
            {previewURL ? (
              <img
                src={previewURL}
                alt="Preview mobile"
                className="w-full rounded border border-gray-300"
                style={{ aspectRatio: '2/1', objectFit: 'cover' }}
              />
            ) : (
              <div
                className="w-full bg-gray-200 rounded flex items-center justify-center"
                style={{ aspectRatio: '2/1' }}
              >
                <MdImage className="text-gray-400 text-3xl" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de recorte */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          imageUrl={imageToCrop}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
          aspectRatio={2}
        />
      )}
    </div>
  );
};

export default BannerImageUpload;
